/**
 * @architecture-header 1
 * @layer module
 * @domain reports
 * @responsibility Render the comment-prompt editor with its preview.
 * @state-owner dataset store holds the saved prompt settings
 * @allowed-imports React; dataset and workspace stores; shared components and hooks; module comment model
 * @forbidden direct API client
 * @public-api reports module
 * @side-effects prompt persistence and toasts
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useRef } from 'react';
import { ModalDocument, SectionLabel } from '../../../shared/components/Modal';
import { useOverlay } from '../../../shared/components/OverlayHost';
import { CLASSES } from '../../../shared/lib/demo-data';
import { answerKey, useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import {
  buildReportComment,
  buildRunComment,
  PROMPT_LENGTHS,
  PROMPT_TONES,
  PROMPT_VARIABLES,
  type PromptKind,
  resolvePrompt,
} from '../model/comment';

export function PromptDialog({ kind }: { kind: PromptKind }) {
  const t = useTranslator();
  const { closeModal } = useOverlay();
  const notify = useWorkspaceUiStore((state) => state.notify);
  const settings = useDatasetStore((state) => state.prompts[kind]);
  const setPrompt = useDatasetStore((state) => state.setPrompt);
  const resetPrompt = useDatasetStore((state) => state.resetPrompt);
  const answers = useDatasetStore((state) => state.answers);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const prompt = resolvePrompt(kind, settings);

  const previewStudent = CLASSES[0].students[kind === 'run' ? 0 : 2];
  const preview =
    kind === 'run'
      ? buildRunComment(
          previewStudent,
          answers[answerKey('r4', previewStudent)]?.marks ?? [],
          prompt,
          t,
        )
      : buildReportComment(previewStudent, prompt, t);

  const insertVariable = (variable: string) => {
    const element = textareaRef.current;
    if (!element) return;
    const value = element.value;
    const caret = element.selectionStart ?? value.length;
    const next = `${value.slice(0, caret)}{${variable}}${value.slice(caret)}`;
    setPrompt(kind, { text: next });
    element.focus();
    window.setTimeout(() => {
      element.selectionStart = caret + variable.length + 2;
      element.selectionEnd = caret + variable.length + 2;
    }, 0);
  };

  return (
    <ModalDocument
      footer={
        <>
          <div className="sp" />
          <button className="bt" type="button" onClick={closeModal}>
            {t('取消')}
          </button>
          <button
            className="bt pri"
            type="button"
            onClick={() => {
              closeModal();
              notify(t('提示詞已儲存，之後產生的評語會照這個寫'));
            }}
          >
            {t('儲存並套用')}
          </button>
        </>
      }
      maxWidth={620}
      subtitle={
        kind === 'run'
          ? t('課後評語 · 每次活動結束後產生')
          : t('學習報告評語 · 跨多次活動的月度評語')
      }
      title={t('評語提示詞')}
    >
      <div className="pmeta">
        {prompt.customised ? (
          <span className="vchip">{t('已客製化')}</span>
        ) : (
          <span className="vmut">{t('目前使用預設')}</span>
        )}
        <div className="sp" />
        <button
          className="lk"
          disabled={!prompt.customised}
          type="button"
          onClick={() => {
            resetPrompt(kind);
            notify(t('已還原成預設提示詞'));
          }}
        >
          {t('還原成預設')}
        </button>
      </div>
      <SectionLabel style={{ marginTop: 12 }}>{t('提示詞')}</SectionLabel>
      <textarea
        className="pbox"
        id="pbox"
        onChange={(event) => setPrompt(kind, { text: event.target.value })}
        ref={textareaRef}
        value={prompt.text}
      />
      <SectionLabel>
        {t('可用變數')}
        <span className="vmut" style={{ fontWeight: 400, marginLeft: 6 }}>
          {t('點一下插入')}
        </span>
      </SectionLabel>
      <div className="pvars">
        {PROMPT_VARIABLES[kind].map(([variable, description]) => (
          <button
            className="pv"
            key={variable}
            title={t(description)}
            type="button"
            onClick={() => insertVariable(variable)}
          >
            {`{${t(variable)}}`}
          </button>
        ))}
      </div>
      <div className="prow">
        <div>
          <SectionLabel style={{ marginTop: 14 }}>{t('語氣')}</SectionLabel>
          <div className="vseg" style={{ margin: 0 }}>
            {PROMPT_TONES.map(([value, label]) => (
              <button
                className={prompt.tone === value ? 'on' : ''}
                key={value}
                type="button"
                onClick={() => setPrompt(kind, { tone: value as 'warm' | 'neutral' | 'strict' })}
              >
                {t(label)}
              </button>
            ))}
          </div>
        </div>
        <div>
          <SectionLabel style={{ marginTop: 14 }}>{t('長度')}</SectionLabel>
          <div className="vseg" style={{ margin: 0 }}>
            {PROMPT_LENGTHS.map(([value, label]) => (
              <button
                className={prompt.length === value ? 'on' : ''}
                key={value}
                type="button"
                onClick={() => setPrompt(kind, { length: value as 'short' | 'mid' | 'long' })}
              >
                {t(label)}
              </button>
            ))}
          </div>
        </div>
      </div>
      <SectionLabel>{t('預覽')}</SectionLabel>
      <div className="ppv">{preview}</div>
    </ModalDocument>
  );
}
